import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  TablePagination,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../utils/IconHandler';
import { formatDate } from '../../utils/DateUtils';
import SupplierBillingStatementService from '../../services/SupplierBillingStatementService';
import ViewReplenishmentDetailsModal from '../../components/Reposition/ViewReplenishmentDetailsModal';
import dayjs from 'dayjs';
import { formatMoney } from '../../utils/currency';
import { permissionOptions } from '../../utils/Enums';

export const hasReplenishmentPermission = () => {
  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  return ['REPLENISHMENT_SUPPLIES', 'REPLENISHMENT_RETAIL_PRODUCTS'].some((label) => {
    const permission = permissionOptions.find((p) => p.label === label);
    return permission && userPermissions.includes(permission.id);
  });
};

const PAID_STATUS = 1;
const PARTIAL_STATUS = 3;

const ViewSupplierBillingStatementModal = ({ open, onClose, statement, onUpdated }) => {
  const { t } = useTranslation();
  const [currentStatement, setCurrentStatement] = useState(statement);
  const [pageRows, setPageRows] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [onlyPending, setOnlyPending] = useState(true);
  const [allPage, setAllPage] = useState(0);
  const [allRowsPerPage, setAllRowsPerPage] = useState(10);
  const requestIdRef = useRef(0);
  const [selectedReplenishment, setSelectedReplenishment] = useState(null);
  const [loadingReplenishmentId, setLoadingReplenishmentId] = useState(null);
  const [detailError, setDetailError] = useState('');

  const fetchReplenishmentDetail = (replenishmentId) =>
    SupplierBillingStatementService.getSupplierReplenishmentDetail(statement.supplierId, replenishmentId);

  const showDetailError = (error) => {
    const status = error?.response?.status;
    setDetailError(
      status === 403 ? 'No tenés permiso para ver el detalle de esta reposición.'
        : status === 404 ? 'La reposición ya no está disponible en este estado de cuenta.'
        : 'No se pudo cargar el detalle de la reposición.'
    );
  };

  const handleViewReplenishment = async (replenishmentId) => {
    setLoadingReplenishmentId(replenishmentId);
    setDetailError('');
    try {
      setSelectedReplenishment(await fetchReplenishmentDetail(replenishmentId));
    } catch (error) {
      showDetailError(error);
    } finally {
      setLoadingReplenishmentId(null);
    }
  };

  // La factura se firma al pedir el detalle (dura minutos), por eso no se guarda en la lista.
  // La pestaña se abre antes del await para que el navegador no la bloquee como popup.
  const handleOpenInvoice = async (replenishmentId) => {
    const tab = window.open('', '_blank');
    setLoadingReplenishmentId(replenishmentId);
    setDetailError('');
    try {
      const detail = await fetchReplenishmentDetail(replenishmentId);
      if (!detail?.invoiceFile) throw new Error('invoice file missing');
      if (tab) tab.location.href = detail.invoiceFile;
      else window.open(detail.invoiceFile, '_blank');
    } catch (error) {
      tab?.close();
      showDetailError(error);
    } finally {
      setLoadingReplenishmentId(null);
    }
  };

  useEffect(() => {
    setCurrentStatement(statement);
    setPageRows(null);
    setTotalRows(0);
    setOnlyPending(true);
    setAllPage(0);
  }, [statement, open]);

  const loadPage = async () => {
    if (!statement?.supplierId) return;
    // Descarta respuestas viejas si cambió la página, el filtro o el proveedor mientras cargaba
    const requestId = ++requestIdRef.current;
    setLoadingAll(true);
    setLoadError(false);
    try {
      const response = await SupplierBillingStatementService.getSupplierReplenishments(
        statement.supplierId, allPage + 1, allRowsPerPage, onlyPending
      );
      if (requestId !== requestIdRef.current) return;
      setPageRows(response.replenishments || []);
      setTotalRows(response.totalCount || 0);
    } catch (error) {
      console.error('Error fetching supplier replenishments page:', error);
      if (requestId !== requestIdRef.current) return;
      setLoadError(true);
      setPageRows([]);
      setTotalRows(0);
    } finally {
      if (requestId === requestIdRef.current) setLoadingAll(false);
    }
  };

  // Paginación y filtro se resuelven en el backend
  useEffect(() => {
    if (open) loadPage();
  }, [open, statement, allPage, allRowsPerPage, onlyPending]);


  if (!currentStatement) return null;

  const formatCurrency = (amount) => {
    return formatMoney(amount);
  };

  const getPaymentStatusText = (status) => {
    if (status === PAID_STATUS) return t('paid') || 'Pagado';
    if (status === PARTIAL_STATUS) return t('partial_payment') || 'Pago parcial';
    return t('pending') || 'Pendiente';
  };

  const getPaymentStatusColor = (status) => {
    if (status === PAID_STATUS) return 'success';
    if (status === PARTIAL_STATUS) return 'info';
    return 'warning';
  };

  // Usar las reposiciones del statement o array vacío si no hay reposiciones
  const replenishments = currentStatement?.replenishments || [];
  
  const displayedReplenishments = pageRows || [];
  // Mientras carga otra página se siguen mostrando las filas actuales (atenuadas) hasta que llegue la data
  const hasReplenishments = displayedReplenishments.length > 0;
  const isChangingPage = loadingAll && pageRows !== null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon('eye')}
        {t('supplier_orders_detail', { defaultValue: 'Detalle de pedidos' })}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('statement_information') || 'Información del Estado de Cuenta'}
          </Typography>
          {/* Información General */}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                {t('supplier') || 'Proveedor'}:
              </Typography>
              <Typography variant="body1">
                {currentStatement.supplierName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                {t('status') || 'Estado'}:
              </Typography>
              <Chip
                label={SupplierBillingStatementService.getStatusText(currentStatement.status, t)}
                color={SupplierBillingStatementService.getStatusColor(currentStatement.status)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                {t('due_date') || 'Fecha de Vencimiento'}:
              </Typography>
              <Typography variant="body1">
                {formatDate(dayjs(currentStatement.dueDate))}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                {t('last_payment') || 'Último Pago'}:
              </Typography>
              <Typography variant="body1">
                {currentStatement.lastPayment ? formatDate(dayjs(currentStatement.lastPayment)) : t('no_payments') || 'Sin pagos'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Resumen Financiero */}
          <Typography variant="h6" gutterBottom>
            {t('financial_summary') || 'Resumen Financiero'}
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {t('total_amount') || 'Monto Total'}
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(currentStatement.totalAmount)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {t('paid_amount') || 'Monto Pagado'}
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(currentStatement.paidAmount)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {t('pending_amount') || 'Monto Pendiente'}
                </Typography>
                <Typography variant="h6" color="error.main">
                  {formatCurrency(currentStatement.pendingAmount)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Reposiciones del Estado de Cuenta */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="h6">
              {t('replenishments') || 'Reposiciones'}
              {pageRows ? ` (${totalRows})` : ''}
            </Typography>
            <FormControlLabel
              control={<Switch checked={onlyPending} onChange={(e) => { setOnlyPending(e.target.checked); setAllPage(0); }} />}
              label="Solo pendientes"
            />
          </Box>
          {detailError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {detailError}
            </Typography>
          )}
          {loadingAll && pageRows === null && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          
          {hasReplenishments ? (
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('invoice') || 'Factura'}</TableCell>
                    <TableCell align="center">{t('date') || 'Fecha'}</TableCell>
                    <TableCell align="center">{t('due_date') || 'Vencimiento'}</TableCell>
                    <TableCell align="center">{t('amount') || 'Monto'}</TableCell>
                    <TableCell align="center">{t('payment_status') || 'Estado de Pago'}</TableCell>
                    <TableCell align="center">{t('location') || 'Ubicación'}</TableCell>
                    <TableCell align="center">{t('actions') || 'Acciones'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isChangingPage && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  )}
                  {displayedReplenishments.map((replenishment) => (
                    <TableRow key={replenishment.id} sx={{ opacity: isChangingPage ? 0.4 : 1, transition: 'opacity 0.15s' }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {replenishment.invoice || `Reposición #${replenishment.id}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {formatDate(dayjs(replenishment.replenishmentDate))}
                      </TableCell>
                      <TableCell align="center">
                        {formatDate(dayjs(replenishment.dueDate))}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(replenishment.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getPaymentStatusText(replenishment.status)}
                          color={getPaymentStatusColor(replenishment.status)}
                          size="small"
                        />
                        {replenishment.status === PARTIAL_STATUS && (
                          <Typography variant="caption" display="block" color="textSecondary">
                            Pagado {formatCurrency(replenishment.paidAmount)} · Resta {formatCurrency(replenishment.pendingAmount)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {replenishment.locationName || `Ubicación #${replenishment.locationId}`}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={t('view_details') || 'Ver detalles'}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={loadingReplenishmentId === replenishment.id}
                              onClick={() => handleViewReplenishment(replenishment.id)}
                            >
                              {getIcon('eye')}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {replenishment.invoiceFile && (
                          <Tooltip title={t('view_invoice') || 'Ver factura'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={loadingReplenishmentId === replenishment.id}
                                onClick={() => handleOpenInvoice(replenishment.id)}
                              >
                                {getIcon('invoice')}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalRows}
                page={allPage}
                onPageChange={(_, newPage) => setAllPage(newPage)}
                rowsPerPage={allRowsPerPage}
                onRowsPerPageChange={(e) => { setAllRowsPerPage(parseInt(e.target.value, 10)); setAllPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </TableContainer>
          ) : loadingAll ? null : (
            <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Box sx={{ fontSize: 48, color: 'text.secondary', display: 'flex' }}>{getIcon('invoice')}</Box>
                <Typography variant="h6" color="text.secondary">
                  {loadError
                    ? 'No se pudieron cargar las reposiciones'
                    : onlyPending
                      ? 'No hay reposiciones pendientes'
                      : t('no_replenishments_found', { defaultValue: 'No se encontraron reposiciones' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {loadError
                    ? 'Intentá de nuevo en unos segundos.'
                    : onlyPending
                      ? 'Este proveedor no tiene reposiciones pendientes de pago.'
                      : t('no_replenishments_description', { defaultValue: 'Este proveedor no tiene reposiciones registradas' })}
                </Typography>
                {loadError && <Button variant="outlined" size="small" onClick={loadPage}>Reintentar</Button>}
              </Box>
            </Paper>
          )}

          {/* Total */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('total') || 'Total'}: {formatCurrency(currentStatement.totalAmount)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('close') || 'Cerrar'}
        </Button>
      </DialogActions>
      <ViewReplenishmentDetailsModal
        open={Boolean(selectedReplenishment)}
        onClose={() => setSelectedReplenishment(null)}
        replenishment={selectedReplenishment}
      />
    </Dialog>
  );
};

export default ViewSupplierBillingStatementModal;
